/**
 * File Types, MIME Types, and Other Constants
 *
 * Copied from scio2: optisigns-portal-common/libs/common/constant/src/lib/app.constant.ts
 */

// Note: FILE_TYPES is defined in ./asset.ts (domain-specific)

// --- File Asset Status ---

export const FILE_ASSET_STATUS = {
  CONVERTING: 'converting',
  FINISHED: 'finished',
  FAILED: 'failed',
  ACTIVE: 'active',
  PENDING: 'pending',
  DOING: 'doing',
  INACTIVE: 'inactive',
  FLASHING: 'flashing',
  WAITING: 'waiting',
} as const;

export type FileAssetStatus = (typeof FILE_ASSET_STATUS)[keyof typeof FILE_ASSET_STATUS];

// --- MIME Type Arrays ---

export const IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
] as const;

export const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/3gpp',
  'video/x-m4v',
  'video/webm',
  'video/x-msvideo',
  'application/vnd.apple.mpegurl',
  'application/x-mpegURL',
  'application/dash+xml',
  'video/quicktime',
  'video/mpeg',
  'video/x-ms-wmv',
] as const;

export const AUDIO_MIME_TYPES = ['audio/mp3', 'audio/mpeg'] as const;

export const DOCUMENT_MIME_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
] as const;

export const PDF_MIME_TYPE = 'application/pdf';

export const SUPPORTED_MIME_TYPES = [
  PDF_MIME_TYPE,
  ...DOCUMENT_MIME_TYPES,
  ...VIDEO_MIME_TYPES,
  ...IMAGE_MIME_TYPES,
  ...AUDIO_MIME_TYPES,
] as const;

// --- Video Status ---

export const VIDEO_STATUS = {
  CONVERTING: 'converting',
  FINISHED: 'finished',
  FAILED: 'failed',
} as const;

export type VideoStatus = (typeof VIDEO_STATUS)[keyof typeof VIDEO_STATUS];

// --- YouTube Types ---

export const YOUTUBE_TYPES = {
  SKY_NEWS: 'skyNews',
  NASA_TV: 'nasaTv',
  YOUTUBE_PLAYLIST: 'youtubePlaylist',
  YOUTUBE_CHANNEL: 'youtubeChannel',
  YOUTUBE_LIVE_SCHEDULE: 'youtubeLiveSchedule',
} as const;

export type YoutubeType = (typeof YOUTUBE_TYPES)[keyof typeof YOUTUBE_TYPES];

// --- UI View Types ---

export const UI_VIEW_TYPES = {
  LIST: 'list',
  GRID: 'grid',
} as const;

export type UiViewType = (typeof UI_VIEW_TYPES)[keyof typeof UI_VIEW_TYPES];

// --- Special Folders ---

export const ON_HOLD_DEVICE_FOLDER = 'OnHold Devices';

// --- Team IDs ---

/** The root (default) team ID — matches scio1's TEAM_TYPES.ROOT */
export const ROOT_TEAM_ID = '1';

// Note: ASSET_TYPES is defined in ./asset.ts (domain-specific)

// --- Modes ---

export const MODES = {
  DEFAULT: 'default',
  SELECT: 'select',
  SEARCH: 'search',
  SEARCH_SELECT: 'search_select',
  SHARE_WITH_ME: 'SHARE_WITH_ME',
  FAVORITES: 'FAVORITES',
  DRIVE: 'DRIVE',
  RECENT: 'RECENT',
  MOVE: 'MOVE',
} as const;

export type Mode = (typeof MODES)[keyof typeof MODES];

// --- Recent Types ---

export const RECENT_TYPES = {
  DEVICE: 'DEVICE',
  PLAYLIST: 'PLAYLIST',
  SCHEDULE: 'SCHEDULE',
  ASSET: 'ASSET',
  ENGAGE: 'ENGAGE',
} as const;

export type RecentType = (typeof RECENT_TYPES)[keyof typeof RECENT_TYPES];

// --- Left Menu ---

export const LEFT_MENU = {
  RECENT: 'recent',
  HOME: 'home',
  FOLDER: 'folder',
  SHARE_WITH_ME: 'SHARE_WITH_ME',
} as const;

export type LeftMenu = (typeof LEFT_MENU)[keyof typeof LEFT_MENU];

// --- Group Actions ---

export const GROUP_ACTIONS = {
  CHANGE_SECURITY: 'changeSecurity',
  VIEW_SECURITY: 'viewSecurity',
  UPDATE_DESCRIPTION: 'updateDescription',
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
  NONE: 'none',
  READ_ONLY: 'readOnly',
} as const;

export type GroupAction = (typeof GROUP_ACTIONS)[keyof typeof GROUP_ACTIONS];

// --- Group Role Types ---

export const GROUP_ROLE_TYPES = {
  OWNER: '0',
  ADMIN: '1',
  USER: '2',
} as const;

export type GroupRoleType = (typeof GROUP_ROLE_TYPES)[keyof typeof GROUP_ROLE_TYPES];

// --- Folder Security Types ---

export const FOLDER_SECURITY_TYPES = {
  EVERYONE_ACCOUNT: '-2',
  EVERYONE_TEAM: '0',
  ONLY_ADMIN: '-1',
} as const;

export type FolderSecurityType = (typeof FOLDER_SECURITY_TYPES)[keyof typeof FOLDER_SECURITY_TYPES];

// --- Emergency Message Types ---

export const EMERGENCY_MESSAGE_DEVICE_TYPE = {
  DEVICE: 'device',
  TAG: 'tag',
  ALL_SCREEN: 'all',
} as const;

export type EmergencyMessageDeviceType =
  (typeof EMERGENCY_MESSAGE_DEVICE_TYPE)[keyof typeof EMERGENCY_MESSAGE_DEVICE_TYPE];

export const EMERGENCY_MESSAGE_CONTENT_TYPE = {
  ASSET: 'asset',
  PLAYLIST: 'playlist',
  TEXT: 'text',
  TEMPLATE: 'template',
} as const;

export type EmergencyMessageContentType =
  (typeof EMERGENCY_MESSAGE_CONTENT_TYPE)[keyof typeof EMERGENCY_MESSAGE_CONTENT_TYPE];

// --- Rotations (display names) ---

export const ROTATIONS = {
  LANDSCAPE: 'Landscape',
  PORTRAIT: 'Portrait',
  CUSTOM: 'Custom',
} as const;

export type Rotation = (typeof ROTATIONS)[keyof typeof ROTATIONS];

// --- Device Priority Audio ---

export const DEVICE_PRIORITY_AUDIO = {
  BACKGROUND_MUSIC: 'backgroundMusic',
  PLAYLIST_VIDEO: 'playlistVideo',
} as const;

export type DevicePriorityAudio =
  (typeof DEVICE_PRIORITY_AUDIO)[keyof typeof DEVICE_PRIORITY_AUDIO];

// --- Device Code Status (numeric codes returned by backend for subscription/addon gating) ---

export const DEVICE_CODE_STATUS = {
  REQUIRED_PLAN: 1004,
  AERICAST_TRIAL: 1000,
  AERICAST_TRIAL_NOT_ENOUGH: 1001,
  AERICAST_PAID_NOT_ENOUGH: 1002,
  ADDON_TRIAL_NOT_ENOUGH: 1005,
  ADDON_NOT_ENOUGH: 1001,
  PUSH_TO_SCREEN_SCHEDULE_EXISTING: 5555,
  PUSH_TO_SCREEN_PLAYLIST: 5556,
  FREE_PLAN_NOT_SUPPORT_DEVICE: 2001,
  MAX_SCREENS_REACHED: 2002,
  ADDON_ENABLE_TRIAL_NOT_ENOUGH: 1006,
  APP_NOT_SUPPORT_ON_DEVICE: 2003,
  ADDON_TIER_MISMATCH: 2002,
} as const;

// --- Device Add-On Compatibility ---

export const DEVICE_BACKGROUND_MUSIC_OS = [
  'tvOS',
  'android',
  'iOS',
  'iPadOS',
  'optisigns_pro',
  'optisigns_pro_max',
] as const;

export const DEVICE_VIDEO_WALL_OS = ['optisigns_pro', 'optisigns_pro_max'] as const;

// --- Helper: Check if MIME type is supported ---

export function isSupportedMimeType(mimeType: string): boolean {
  return (SUPPORTED_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isImageMimeType(mimeType: string): boolean {
  return (IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isVideoMimeType(mimeType: string): boolean {
  return (VIDEO_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isAudioMimeType(mimeType: string): boolean {
  return (AUDIO_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isDocumentMimeType(mimeType: string): boolean {
  return (
    mimeType === PDF_MIME_TYPE || (DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType)
  );
}
